import { NgClass } from '@angular/common';
import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { SystemIconsComponent } from '@shared/components/ui/system-icons';
import { QrCodeComponent } from '@shared/components/ui/qr-code';
import { UiStore } from '@theme/data-access';
import { DeviceLoginStore } from '@live-pickup/data-access';
import { DeviceLoginStatus } from '@auth/data-access';
import { ThemeSwitcherComponent } from '@live-pickup/presentation';
import { AuroraBackgroundComponent } from './aurora-background.component';

type SignInMethod = 'phone' | 'remote';

@Component({
  selector: 'lib-live-pickup-sign-in',
  standalone: true,
  imports: [AuroraBackgroundComponent, SystemIconsComponent, QrCodeComponent, ThemeSwitcherComponent, NgClass, TranslateModule],
  templateUrl: './sign-in.component.html',
  styleUrl: './sign-in.component.scss',
})
export class SignInComponent implements OnInit, OnDestroy {
  private readonly uiStore = inject(UiStore);
  private readonly deviceLoginStore = inject(DeviceLoginStore);

  readonly method = signal<SignInMethod>('phone');
  readonly isMobile = this.uiStore.isMobile;

  readonly pairingUrl = computed(
    () => this.deviceLoginStore.verificationUriComplete() ?? '',
  );
  readonly pairingCode = this.deviceLoginStore.formattedActivationCode;
  readonly isStarting = this.deviceLoginStore.startSessionLoading;
  readonly hasError = this.deviceLoginStore.startSessionError;

  readonly logoWidth = computed(() => (this.isMobile() ? 88 : 120));
  readonly qrWidth = computed(() => (this.isMobile() ? 200 : 240));
  readonly qrLogoSize = computed(() => (this.isMobile() ? 44 : 56));

  readonly auroraColorStops = computed(() => {
    const isDark = this.uiStore.theme().dark;
    return isDark
      ? ['#0D7BFF', '#0961CC', '#1e40af']
      : ['#E0F2FE', '#BAE6FD', '#E0F2FE'];
  });

  ngOnInit(): void {
    this.deviceLoginStore.startSession();
    document.addEventListener('visibilitychange', this.handleResume);
    window.addEventListener('pageshow', this.handleResume);
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.handleResume);
    window.removeEventListener('pageshow', this.handleResume);
    this.deviceLoginStore.cancelSession();
    this.deviceLoginStore.reset();
  }

  /**
   * On a TV the browser is often frozen while the panel is off, so the
   * auto-refresh timer can resume without accounting for the elapsed wall-clock
   * time and leave a stale/expired pairing code on screen. When the page
   * becomes visible again, re-issue the code if it has expired (kiosk has no
   * one to press a button).
   */
  private readonly handleResume = (): void => {
    if (document.visibilityState !== 'visible') return;
    if (this.deviceLoginStore.startSessionLoading()) return;
    if (this.deviceLoginStore.status() === DeviceLoginStatus.Approved) return;
    if (this.deviceLoginStore.isSessionStale()) {
      this.deviceLoginStore.startSession();
    }
  };

  selectMethod(method: SignInMethod): void {
    this.method.set(method);
  }

  retry(): void {
    this.deviceLoginStore.startSession();
  }
}
